import {
  AccountParticipationLevel,
  CashInCashOutMethod,
  CashInCashOutType,
  patchUpdateCashInCashOutMethod,
} from '@nemuru-eng/js-api-client';
import { Dispatch, SetStateAction } from 'react';

import { BankAccountDetailsForm } from '@/components/modules/BorrowerAccountDetails/components/BankAccountDetailsForm';
import {
  BankAccountDetailsFormSchema,
  BankAccountDetailsFormType,
} from '@/components/modules/BorrowerAccountDetails/schemas/BankAccountDetailsFormSchema';
import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { useRequest } from '@/hooks/requests/useRequest';
import { removeNullKeysFromObject } from '@/utils/removeNullKeysFromObject';

interface BankAccountDetailsEditDialogProps {
  cashInCashOutMethod: CashInCashOutMethod;
  revalidateCashInCashOutMethod: () => Promise<CashInCashOutMethod>;
  opened: boolean;
  setOpened: Dispatch<SetStateAction<boolean>>;
  toggleDialog: () => void;
}

export const BankAccountDetailsEditDialog = ({
  cashInCashOutMethod,
  revalidateCashInCashOutMethod,
  opened,
  setOpened,
  toggleDialog,
}: BankAccountDetailsEditDialogProps) => {
  const { request, loading } = useRequest();

  const bankAccountDetailsDefaultValues = {
    accountNumber: cashInCashOutMethod.accountNumber,
    accountHolderFirstName: cashInCashOutMethod.accountHolderFirstName,
    accountHolderFirstLastName: cashInCashOutMethod.accountHolderFirstLastName,
    accountHolderSecondLastName: cashInCashOutMethod.accountHolderSecondLastName,
    accountHolderParticipationLevel: cashInCashOutMethod.accountHolderParticipationLevel,
    description: cashInCashOutMethod.description,
    type: cashInCashOutMethod.type,
  };

  const onSubmitForm = async (useFormValues: BankAccountDetailsFormType) => {
    const updateCashInCashOutMethodBody: PatchUpdateCashInCashOutMethod = removeNullKeysFromObject({
      id: cashInCashOutMethod.id,
      accountHolderFirstName: useFormValues.accountHolderFirstName,
      accountHolderLastName: useFormValues.accountHolderSecondLastName
        ? `${useFormValues.accountHolderFirstLastName} ${useFormValues.accountHolderSecondLastName}`
        : (useFormValues.accountHolderFirstLastName as string),
      description: useFormValues.description ? useFormValues.description : null,
      accountHolderCode: useFormValues.type !== CashInCashOutType.FLYWIRE ? useFormValues.accountNumber : null,
      type: useFormValues.type,
      accountHolderAccountParticipationLevel: useFormValues.accountHolderParticipationLevel,
    });
    await request(patchUpdateCashInCashOutMethod, updateCashInCashOutMethodBody);
    toggleDialog();
    revalidateCashInCashOutMethod();
  };

  return (
    <EditDialogWrapper
      opened={opened}
      setOpened={setOpened}
      handleClose={toggleDialog}
      DialogProps={{
        maxWidth: 'sm',
        fullWidth: true,
      }}
      title={'EDIT_BANK_ACCOUNT'}
      onSubmitForm={onSubmitForm}
      isLoading={loading}
      defaultValues={bankAccountDetailsDefaultValues}
      formSchema={BankAccountDetailsFormSchema}
    >
      <BankAccountDetailsForm cashInCashOutMethod={cashInCashOutMethod} isEditable={true} />
    </EditDialogWrapper>
  );
};

export interface PatchUpdateCashInCashOutMethod {
  id: string;
  accountHolderFirstName: string;
  accountHolderLastName: string;
  description: string | null;
  type: CashInCashOutType;
  accountHolderAccountParticipationLevel: AccountParticipationLevel;
  accountHolderCode: string | null;
}
